import AddIcon from '@mui/icons-material/Add';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { useAppDispatch } from '@/app/store/hooks';
import { ImageViewerDialog } from '@/components/feedback/ImageViewerDialog';
import { DetailCard, DetailCardBody, DetailCardHeader } from '@/components/layout/DetailCard';
import {
  useDeleteSignatureMutation,
  useGetSignaturesQuery,
  type Signature,
} from '@/features/annual-review';
import { toastShown } from '@/features/ui';

import { AddSignatureDialog } from './AddSignatureDialog';
import { SignatureGallery } from './SignatureGallery';

/**
 * The director's saved signature images.
 *
 * Shown as a gallery rather than the old portal's select-and-preview: a
 * dropdown forces a director to open each option to find the one they want to
 * delete, and there are rarely more than three. Seeing them all at once is both
 * fewer taps and less to explain.
 *
 * The last remaining signature cannot be removed. Signing is what this portal
 * is for, and a director with none has to be re-invited to sign something
 * before they can create one — so the delete is blocked at the only point where
 * the count is known rather than left to the API to reject.
 */
export const SignaturesCard = () => {
  const dispatch = useAppDispatch();
  const { data: signatures = [], isLoading, isError } = useGetSignaturesQuery(undefined);
  const [deleteSignature, { isLoading: isDeleting }] = useDeleteSignatureMutation();
  const [pendingRemoval, setPendingRemoval] = useState<Signature | null>(null);
  const [viewing, setViewing] = useState<{ signature: Signature; label: string } | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // `<= 1` rather than `=== 1`: the confirmation can outlive a refetch that
  // empties the list, and a count of zero must block the delete too.
  const isLastSignature = signatures.length <= 1;

  const handleConfirmRemoval = async () => {
    if (pendingRemoval === null) return;

    // The dialog is unreachable while one signature is left, so this is the
    // guard against the list shrinking under an already-open confirmation.
    if (isLastSignature) {
      dispatch(toastShown('Keep at least one signature so you can sign documents.', 'error'));
      setPendingRemoval(null);
      return;
    }

    try {
      await deleteSignature({ signatureId: pendingRemoval.id }).unwrap();
      dispatch(toastShown('Signature removed.', 'success'));
      setPendingRemoval(null);
    } catch {
      dispatch(toastShown('That signature could not be removed.', 'error'));
    }
  };

  return (
    <DetailCard>
      <DetailCardHeader
        icon={<DrawOutlinedIcon />}
        title="Signatures"
        subtitle="Used when you sign documents and annual reviews"
        action={
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              setIsAdding(true);
            }}
            variant="outlined"
            size="small"
            className="w-full sm:w-auto"
          >
            Add signature
          </Button>
        }
      />

      <DetailCardBody className="flex flex-col gap-3 p-4 sm:p-5">
        <SignatureGallery
          signatures={signatures}
          isLoading={isLoading}
          isError={isError}
          isDeleting={isDeleting}
          onView={(signature, label) => {
            setViewing({ signature, label });
          }}
          onRemove={setPendingRemoval}
        />

        {/* Exactly one, not `isLastSignature`: an empty list is also "one or
            fewer", and the notice would then claim a signature that is not
            there. The gallery renders its own empty state for that case. */}
        {signatures.length === 1 && (
          <div className="flex items-start gap-2 rounded-lg bg-surface-subtle px-3 py-2">
            <InfoOutlinedIcon fontSize="small" className="mt-px shrink-0 text-content-muted" />
            <Typography variant="caption" color="text.secondary" component="p">
              This is your only signature, so it cannot be removed. Add another one first.
            </Typography>
          </div>
        )}
      </DetailCardBody>

      <AddSignatureDialog
        open={isAdding}
        onClose={() => {
          setIsAdding(false);
        }}
      />

      <ImageViewerDialog
        open={viewing !== null}
        onClose={() => {
          setViewing(null);
        }}
        src={viewing?.signature.base64Image ?? ''}
        alt={viewing?.label ?? ''}
        title={viewing?.label ?? ''}
        onPlate
      />

      <Dialog
        open={pendingRemoval !== null}
        onClose={() => {
          setPendingRemoval(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Remove this signature?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Documents you have already signed keep the signature they were signed with.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="flex-col-reverse gap-2 p-4 sm:flex-row sm:justify-end">
          <Button
            onClick={() => {
              setPendingRemoval(null);
            }}
            disabled={isDeleting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirmRemoval()}
            color="error"
            variant="contained"
            disabled={isDeleting}
            className="w-full sm:w-auto"
          >
            {isDeleting ? 'Removing…' : 'Remove signature'}
          </Button>
        </DialogActions>
      </Dialog>
    </DetailCard>
  );
};
